import React from "react";

import { DateComparison } from "@graffer-inc/form-schema";

type Props = {
  identifier: string;
  index: number;
  value: DateComparison;
};

export const AllowedTermInput: React.FC<Props> = ({
  identifier,
  index,
  value,
}) => {
  return (
    <div className="columns px-4 py-4">
      <div
        className="column is-full mb-4 is-paddingless"
        style={{
          border: "1px solid #CFCFCF",
          borderRadius: 3,
        }}
      >
        <div className="columns is-marginless has-background-light px-4">
          <div
            className="column is-paddingless my-4"
            style={{
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
            }}
          >
            <label className="label">{`# ${index + 1}`}</label>
          </div>
        </div>

        <div className="columns px-4 py-4">
          <div className="column">
            <div className="field">
              <label className="label">定義内容</label>
              <div className="control">
                <label className="radio">
                  <input
                    type="radio"
                    name={`${identifier}_dateComparison_definedAs`}
                    checked={value.definedAs === "min"}
                    disabled
                  />
                  {textMapForDefinedAs["min"]}
                </label>
                <label className="radio">
                  <input
                    type="radio"
                    name={`${identifier}_dateComparison_definedAs`}
                    checked={value.definedAs === "max"}
                    disabled
                  />
                  {textMapForDefinedAs["max"]}
                </label>
              </div>
            </div>

            <div
              className={`field ${
                value.basedOn.kind === "FormSubmissionDate" ? "mb-4" : ""
              }`}
            >
              <label className="label">計算基準日</label>
              <div className="control">
                <label className="radio">
                  <input
                    type="radio"
                    name={`${identifier}_dateComparison_basedOn_kind`}
                    checked={value.basedOn.kind === "FormSubmissionDate"}
                    disabled
                  />
                  フォームが送信された日付
                </label>
                <label className="radio">
                  <input
                    type="radio"
                    name={`${identifier}_dateComparison_basedOn_kind`}
                    checked={value.basedOn.kind === "FixedDate"}
                    disabled
                  />
                  固定の日付
                </label>
              </div>
            </div>

            {value.basedOn.kind === "FixedDate" && (
              <FixedDateValueInput value={value.basedOn.value} />
            )}

            <div className="field mb-4">
              <label className="label">
                {textMapForDefinedAs[value.definedAs]}の制限をどこへ設けるか
              </label>
              <div className="control">
                <label className="radio">
                  <input
                    type="radio"
                    name={`${identifier}_dateComparison_arithmeticOperation`}
                    checked={value.arithmeticOperation === "minus"}
                    disabled
                  />
                  基準日以前
                </label>
                <label className="radio">
                  <input
                    type="radio"
                    name={`${identifier}_dateComparison_arithmeticOperation`}
                    checked={value.arithmeticOperation === "plus"}
                    disabled
                  />
                  基準日以後
                </label>
              </div>
            </div>

            <div className="field mb-4">
              <label className="label">計算に用いる年の値（数字）</label>
              <div className="control">
                <p>{value.year.value}</p>
              </div>
            </div>

            <div className="field">
              <label className="label">計算に用いる月の値（数字）</label>
              <div className="control">
                <p>{value.month.value}</p>
              </div>
            </div>
            <div className="field mb-4">
              <div className="control">
                <label className="radio">
                  <input
                    type="radio"
                    name={`${identifier}_dateComparison_month_usedAs`}
                    checked={value.month.usedAs === "relative"}
                    disabled
                  />
                  基準日からの差として扱う
                </label>
                <label className="radio">
                  <input
                    type="radio"
                    name={`${identifier}_dateComparison_month_usedAs`}
                    checked={value.month.usedAs === "absolute"}
                    disabled
                  />
                  絶対的な値として扱う
                </label>
              </div>
            </div>

            {value.day && (
              <CalcDayValueEditor
                identifier={identifier}
                value={value}
                day={value.day}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CalcDayValueEditor: React.FC<
  Pick<Props, "identifier" | "value"> & {
    day: Exclude<Props["value"]["day"], undefined>;
  }
> = ({ identifier, value, day }) => {
  return (
    <>
      <div className="field">
        <label className="label">計算に用いる日の値（数字）</label>
        <div className="control">
          <p>{day.value}</p>
        </div>
      </div>
      <div className="field">
        <div className="control">
          <label className="radio">
            <input
              type="radio"
              name={`${identifier}_dateComparison_day_usedAs`}
              checked={day.usedAs === "relative"}
              disabled
            />
            基準日からの差として扱う
          </label>
          <label className="radio">
            <input
              type="radio"
              name={`${identifier}_dateComparison_day_usedAs`}
              checked={day.usedAs === "absolute"}
              disabled
            />
            絶対的な値として扱う
          </label>
        </div>
        <HelpTextForDayValue day={day} definedAs={value.definedAs} />
      </div>
    </>
  );
};

const textMapForDefinedAs: Record<DateComparison["definedAs"], string> = {
  min: "最小値",
  max: "最大値",
} as const;

type FixedDateValueInputProps = {
  value: string;
};

const FixedDateValueInput: React.FC<FixedDateValueInputProps> = ({ value }) => {
  return (
    <div className="field has-addons mb-4">
      <div className="control">
        <p>{value.split("-")[0]}年1月1日</p>
      </div>
    </div>
  );
};

const HelpTextForDayValue: React.FC<
  Pick<Required<DateComparison>, "day" | "definedAs">
> = ({ day, definedAs }) => {
  const { value, usedAs } = day;

  if (usedAs !== "absolute") {
    return <></>;
  }

  if (value <= 28) {
    return <></>;
  }

  return (
    <p className="help mt-2">{`${value}日よりも短い月の場合は、月末日が${textMapForDefinedAs[definedAs]}として設定されます。`}</p>
  );
};
